import React, { useEffect, useState } from 'react';
import { useSettings } from '../../hooks/useSettings';

const apiBase = process.env.REACT_APP_BACKEND_URL;

const fetchJSON = async (url, opts = {}) => {
  const adminToken = localStorage.getItem('admin_token');
  const res = await fetch(`${apiBase}${url}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${adminToken}`,
      ...(opts.headers || {})
    }
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

const PartnerWalletPartnerTab = () => {
  const [wallet, setWallet] = useState(null);
  const [packages, setPackages] = useState([]);
  const [topups, setTopups] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [checkoutMessage, setCheckoutMessage] = useState(null);

  const { safeSettings } = useSettings();
  const displayTz = safeSettings?.timezone || 'UTC';
  
  // Format date with timezone (treating DB time as UTC)
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      // Ensure the date is treated as UTC by appending 'Z' if not present
      const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
      const d = new Date(utcDateString);
      
      return d.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: displayTz
      });
    } catch (e) {
      console.error('Error formatting date:', e);
      return dateString;
    }
  };

  const loadWallet = async () => {
    try {
      const data = await fetchJSON('/api/admin/partner/wallet');
      setWallet(data);
    } catch (e) { console.error(e); }
  };
  const loadPackages = async () => {
    try {
      const data = await fetchJSON('/api/admin/partner/topup-packages');
      setPackages(data.items || []);
    } catch (e) { console.error(e); }
  };
  const loadTopups = async () => {
    try {
      const params = new URLSearchParams({ page: String(page), page_size: '10' });
      const data = await fetchJSON(`/api/admin/partner/topups/paged?${params.toString()}`);
      setTopups(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { console.error(e); }
  };

  // Handle checkout URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const checkout = urlParams.get('checkout');
    const topupId = urlParams.get('topup_id');
    
    if (checkout) {
      switch (checkout) {
        case 'manual':
          setCheckoutMessage({
            type: 'info',
            message: 'Manual payment selected. Please contact support to complete your top-up with bank transfer details.'
          });
          break;
        case 'success':
          setCheckoutMessage({
            type: 'success',
            message: `Payment successful! Your wallet will be updated shortly. ${topupId ? `Transaction ID: ${topupId}` : ''}`
          });
          // Refresh wallet data after successful payment
          loadWallet();
          loadTopups();
          break;
        case 'cancel':
          setCheckoutMessage({
            type: 'warning',
            message: 'Payment was cancelled. You can try again anytime.'
          });
          break;
        case 'error':
          setCheckoutMessage({
            type: 'error',
            message: 'Payment failed. Please try again or contact support if the issue persists.'
          });
          break;
      }
      
      // Clear URL parameters after showing message
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  useEffect(() => { 
    loadWallet(); 
    loadPackages(); 
  }, []);
  useEffect(() => { loadTopups(); }, [page]);

  const handleBuy = async (package_id) => {
    try {
      const res = await fetchJSON('/api/admin/partner/topups/initiate', { method: 'POST', body: JSON.stringify({ package_id }) });
      if (res?.mode === 'redirect' && res?.url) {
        window.location.href = res.url;
      }
    } catch (e) {
      alert('Unable to initiate top-up.');
      console.error(e);
    }
  };

  return (
    <div className="p-6">
      {/* Checkout Message */}
      {checkoutMessage && (
        <div className={`mb-6 p-4 rounded-lg ${
          checkoutMessage.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' :
          checkoutMessage.type === 'error' ? 'bg-red-50 border border-red-200 text-red-800' :
          checkoutMessage.type === 'warning' ? 'bg-yellow-50 border border-yellow-200 text-yellow-800' :
          'bg-blue-50 border border-blue-200 text-blue-800'
        }`}>
          <div className="flex justify-between items-start">
            <p>{checkoutMessage.message}</p>
            <button 
              onClick={() => setCheckoutMessage(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Wallet Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500">Credits Balance</div>
          <div className="text-3xl font-semibold text-gray-900 mt-1">{wallet ? wallet.credits_balance : '—'}</div>
          <div className="text-xs text-gray-400 mt-2">Updated: {formatDate(wallet?.updated_at)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500">Last Top-up</div>
          <div className="text-lg text-gray-900 mt-1">{formatDate(wallet?.last_topup_at)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500">Timezone</div>
          <div className="text-lg text-gray-900 mt-1">{displayTz}</div>
        </div>
      </div>

      {/* Packages Grid */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Available Packages</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {packages.map(p => (
            <div key={p.id} className="border rounded-lg p-4 flex flex-col">
              <div className="font-semibold text-gray-900">{p.name}</div>
              <div className="text-sm text-gray-500 mb-1">{p.description}</div>
              <div className="text-sm">Credits: <span className="font-medium">{p.credits}</span></div>
              <div className="text-sm">Price: <span className="font-medium">{p.currency} {p.price}</span></div>
              <button className="mt-3 px-4 py-2 bg-indigo-600 text-white rounded" onClick={() => handleBuy(p.id)}>Buy</button>
            </div>
          ))}
        </div>
      </div>

      {/* History */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Top-ups History</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="text-left text-sm text-gray-500">
                <th className="p-2">Created</th>
                <th className="p-2">Credits</th>
                <th className="p-2">Amount</th>
                <th className="p-2">Currency</th>
                <th className="p-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {topups.map(t => (
                <tr key={t.id} className="border-t">
                  <td className="p-2 text-sm text-gray-700">{formatDate(t.created_at)}</td>
                  <td className="p-2">{t.credits}</td>
                  <td className="p-2">{t.amount}</td>
                  <td className="p-2">{t.currency}</td>
                  <td className="p-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      t.status === 'paid' ? 'bg-green-100 text-green-800' :
                      t.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      t.status === 'failed' ? 'bg-red-100 text-red-800' :
                      t.status === 'refunded' ? 'bg-gray-100 text-gray-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {t.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-500">Total: {total}</div>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
            <span className="px-2">Page {page}</span>
            <button disabled={(page * 10) >= total} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerWalletPartnerTab;